import { GridCellParams, GridRowId, GridGroupNode, useGridApiRef } from '@enterprise/material-data-grid';
import { CombinedItem } from '../../../../../core/models';
import { get, head, isEmpty } from 'lodash';
import { ScreenSizeType, useScreenSize } from '../../../../../hooks';
import { CombinedItemsGridColumn, useCombinedItemGridColumns } from './useCombinedItemsGridColumns.hook';
import { useCombinedItemGridState } from './useCombinedItemsGridState.hook';
import { CombinedItemEditQuery, CombinedItemGridState } from '../../../../../store';

export interface UseItemGridProps {
    columnsState?: CombinedItemGridState;
    onColumnsStateChange?: (state: CombinedItemGridState) => void;
}

export const useCombinedItemGrid = (params: UseItemGridProps) => {
    const { isUpTo } = useScreenSize();
    const apiRef = useGridApiRef();
    const { isAutoGenerated, columnNames, columnSizes, columns, groupColumn } = useCombinedItemGridColumns({ apiRef });
    const state = useCombinedItemGridState({ apiRef, columnSizes, ...params });

    const getGroupGeneratedRowNodes = (rowId?: GridRowId | null): GridGroupNode[] => {
        if (!rowId) {
            return [];
        }

        const node = apiRef.current.getRowNode(rowId) as GridGroupNode;
        const parentGroupRowNodes = getGroupGeneratedRowNodes(node?.parent);

        if (node?.isAutoGenerated) {
            return [node, ...parentGroupRowNodes];
        }

        return [...parentGroupRowNodes];
    };

    const getQueryValue = (node: GridGroupNode): Partial<CombinedItemEditQuery> => {
        const { id, groupingField } = node;
        const rowIds = apiRef.current?.getRowGroupChildren({ groupId: id, applyFiltering: true, applySorting: true });
        const rowId = head(rowIds);
        const combonedItem = rowId && apiRef.current?.getRow<CombinedItem>(rowId);

        switch (groupingField) {
            case CombinedItemsGridColumn.Id:
                return { id: get(combonedItem, CombinedItemsGridColumn.Id) };
            case CombinedItemsGridColumn.Description:
                return { description: get(combonedItem, CombinedItemsGridColumn.Description) };
            default:
                return {};
        }
    };

    const getGroupQuery = (rowId: GridRowId | null): CombinedItemEditQuery | undefined => {
        const generatedRowNodes = getGroupGeneratedRowNodes(rowId);

        if (!generatedRowNodes.length) {
            return;
        }

        const query = generatedRowNodes.reduce((agg, node) => {
            return { ...agg, ...getQueryValue(node) };
        }, {} as CombinedItemEditQuery);

        return (isEmpty(query) && undefined) || query;
    };

    const getInvoiceItemQuery = (rowId: GridRowId | null): CombinedItemEditQuery | undefined => {
        if (!rowId) {
            return;
        }

        const combinedItem = apiRef.current.getRow<CombinedItem>(rowId);
        if (!combinedItem) {
            return;
        }

        const query: CombinedItemEditQuery = {
            id: combinedItem[CombinedItemsGridColumn.Id],
            description: combinedItem[CombinedItemsGridColumn.Description],
            // isSub: Boolean(combinedItem.itemParent?.pimsGeneratedId),
            practice: combinedItem.provider.siteId,
        };

        return query;
    };

    const getQuery = (params: GridCellParams): CombinedItemEditQuery | undefined => {
        const { id } = params;

        if (!isAutoGenerated(id)) {
            return getInvoiceItemQuery(id);
        }

        if (isAutoGenerated(id)) {
            return getGroupQuery(id);
        }

        return;
    };

    return {
        apiRef,
        isAutoGenerated,
        getQuery,
        columnNames,
        columns,
        groupColumn,
        isTabletView: isUpTo(ScreenSizeType.md),
        state,
    };
};
